/*
 * Copyright (C) 2018 - 2023 Entgra (Pvt) Ltd, Inc - All Rights Reserved.
 *
 * Unauthorised copying/redistribution of this file, via any medium is strictly prohibited.
 *
 * Licensed under the Entgra Commercial License, Version 1.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *       https://entgra.io/licenses/entgra-commercial/1.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */
import React, { Component } from 'react';
// Load locale-specific relative date/time formatting rules.
import { withTranslation } from 'react-i18next';
import { PageHeader, Tabs, Typography, notification } from 'antd';
import Details from '../Details';
import { DeploymentUnitOutlined } from '@ant-design/icons';
import BreadcrumbComponent from '../../../../../../../components/BreadCrumb';
import styles from './styles.module.css';
import { withConfigContext } from '../../../../../../../components/ConfigContext';
import TreeHierarchy from '../TreeHierarchy';
import { handleApiError } from '../../../../../../../services/utils/errorHandler';
import axios from 'axios';
import striptags from 'striptags';

const { Paragraph, Title } = Typography;

const TabPane = Tabs.TabPane;
class SingleGroup extends Component {
  constructor(props) {
    super(props);
    this.config = props.context;
    this.state = {
      activeTab: '1',
      group: null,
      shareRolesData: null,
      quilName: '',
      quilDescription: '',
    };
  }

  fetchGroup = () => {
    let { t } = this.props;
    const { groupId } = this.props.match.params;
    let apiUrl =
      window.location.origin +
      this.config.serverConfig.invoker.uri +
      this.config.serverConfig.invoker.deviceMgt +
      `/groups/id/${groupId}?allowed=true`;

    axios
      .get(apiUrl)
      .then(result => {
        if (result.status === 200) {
          const group = result.data.data;
          this.setState({
            group: { ...group },
            quilName: group.name,
            quilDescription: group.description,
          });
        }
      })
      .catch(error => {
        handleApiError(error, t('api_retrieveDeviceDetailsError'), t);
      });
  };

  fetchSharedRoles = () => {
    const config = this.props.context;
    const { t } = this.props;
    const { groupId } = this.props.match.params;
    const apiUrl =
      window.location.origin +
      config.serverConfig.invoker.uri +
      config.serverConfig.invoker.deviceMgt +
      '/groups/id/' +
      groupId +
      '/roles';

    axios
      .get(apiUrl)
      .then(res => {
        if (res.status === 200) {
          this.setState({
            shareRolesData: [...res.data.data.roles],
          });
        }
      })
      .catch(error => {
        handleApiError(error, t('api_loadShareRolesError'), t);
      });
  };

  componentDidUpdate(prevProps, prevState) {
    if (prevProps.match.params.groupId !== this.props.match.params.groupId) {
      this.fetchGroup();
      this.fetchSharedRoles();
    }
  }

  componentDidMount() {
    this.fetchGroup();
    this.fetchSharedRoles();
  }

  routes = (appName, t) => {
    return [
      {
        path: `/${appName}`,
        breadcrumbName: t('home_menu_home'),
      },
      {
        path: `/${appName}/groups`,
        breadcrumbName: t('home_menu_groups'),
      },
      {
        breadcrumbName: t('label_sub_groups'),
      },
    ];
  };

  updateShareRolesData = shareRolesData => {
    this.setState({ shareRolesData: [...shareRolesData] });
  };

  handleUpdate = () => {
    const config = this.props.context;
    const { t } = this.props;
    const name = striptags(this.state.quilName),
      description = striptags(this.state.quilDescription);
    const group = { ...this.state.group, name, description };

    axios
      .put(
        window.location.origin +
          config.serverConfig.invoker.uri +
          config.serverConfig.invoker.deviceMgt +
          '/groups/id/' +
          group.id,
        group,
      )
      .then(res => {
        if (res.status === 200) {
          this.setState({ group });
          notification.success({
            message: t('api_successMsg'),
            duration: 4,
            description: t('api_updateGroupMsg'),
          });
        }
      })
      .catch(error => {
        this.setState({
          quilDescription: group.description,
          quilName: group.name,
        });
        handleApiError(
          error,
          t('api_updateError', { label: t('home_menu_group') }),
          t,
        );
      });
  };

  handleDescriptionChange = quilDescription => {
    this.setState({ quilDescription });
  };

  handleNameChange = quilName => {
    this.setState({ quilName });
  };

  handleTreeHierarchyButtonClick = group => {
    // Update the activeTab state to switch to the first tab and set the children state
    this.setState({
      activeTab: '1',
      children: group.childrenGroups,
    });
  };

  render() {
    const { t } = this.props;
    const { group, shareRolesData, quilName, quilDescription } = this.state;
    return (
      <div>
        <PageHeader
          className={styles.pageHeader}
          title={
            <Title level={1}>
              <span className={styles.icon}>
                <DeploymentUnitOutlined />
              </span>
              {t('label_deviceGroups')}
            </Title>
          }
          ghost={false}
          breadcrumb={
            <BreadcrumbComponent
              breadcrumbList={this.routes(this.config.appName, t)}
            />
          }
        >
          <div>
            <Paragraph className={styles.description}>
              {t('groups_description')}
            </Paragraph>
          </div>
        </PageHeader>

        <div className={styles.table}>
          <div>
            <Tabs
              activeKey={this.state.activeTab} // Set the activeTab state as the activeKey
              onChange={key => this.setState({ activeTab: key })} // Update the activeTab state on tab change
            >
              <TabPane tab={t('label_details')} key="1">
                {group && shareRolesData && (
                  <Details
                    group={group}
                    shareRolesData={shareRolesData}
                    updateShareRolesData={this.updateShareRolesData}
                    handleUpdate={this.handleUpdate}
                    handleDescriptionChange={this.handleDescriptionChange}
                    handleNameChange={this.handleNameChange}
                    quilName={quilName}
                    quilDescription={quilDescription}
                  />
                )}
              </TabPane>
              <TabPane tab={t('label_hierarchical_view')} key="3">
                <TreeHierarchy
                  onButtonClick={this.handleTreeHierarchyButtonClick}
                />
              </TabPane>
            </Tabs>
          </div>
        </div>
      </div>
    );
  }
}

export default withConfigContext(withTranslation()(SingleGroup));
